
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { PuttingResult } from "@/types/puttingResults";

// Maximum number of retry attempts
const MAX_RETRIES = 3;

export const useDeletePuttingResult = () => {
  const queryClient = useQueryClient();

  const { mutate: deletePuttingResult, isPending } = useMutation({
    mutationFn: async (id: string) => {
      let retries = 0;
      let deleted = false;
      
      // Retry deletion function with exponential backoff
      const retryDelete = async (): Promise<string> => {
        try {
          // Try to delete from putting_test_results first
          const { error: puttingError, data: puttingData } = await supabase
            .from('putting_test_results')
            .delete()
            .eq('id', id)
            .select();
    
          if (puttingError) {
            console.error("Error deleting from putting_test_results:", puttingError);
            throw puttingError;
          }

          if (puttingData && puttingData.length > 0) {
            deleted = true;
            console.log("Successfully deleted from putting_test_results");
            return id;
          }

          // If no records were deleted, try level_tests_results
          console.log("Not found in putting_test_results, trying level_tests_results");
          
          const { error: levelError, data: levelData } = await supabase
            .from('level_tests_results')
            .delete()
            .eq('id', id)
            .select();
          
          if (levelError) {
            console.error("Error deleting from level_tests_results:", levelError);
            throw levelError;
          }

          if (levelData && levelData.length > 0) {
            deleted = true;
            console.log("Successfully deleted from level_tests_results");
            return id;
          }

          // If still not found, throw an error
          throw new Error("Aucun résultat trouvé avec cet ID");
        } catch (error) {
          retries++;
          console.error(`Delete attempt ${retries} failed:`, error);
          
          if (retries < MAX_RETRIES && !deleted) {
            // Exponential backoff: 1s, 2s, 4s
            const delay = 1000 * Math.pow(2, retries - 1);
            await new Promise(resolve => setTimeout(resolve, delay));
            return retryDelete(); // Recursively retry
          } else {
            throw error;
          }
        }
      };
      
      return retryDelete();
    },
    onSuccess: (deletedId) => {
      toast({
        title: "Résultat supprimé",
        description: "Le résultat a été supprimé avec succès",
      });

      // Implement optimistic updates - update both query caches
      queryClient.setQueryData(
        ["putting-results"],
        (oldData: PuttingResult[] | undefined) => 
          oldData ? oldData.filter(item => item.id !== deletedId) : []
      );
      
      queryClient.setQueryData(
        ["training-results"],
        (oldData: PuttingResult[] | undefined) => 
          oldData ? oldData.filter(item => item.id !== deletedId) : []
      );
    },
    onError: (error) => {
      console.error("Error deleting result:", error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le résultat",
        variant: "destructive",
      });
      
      // Refetch the data if the optimistic update failed
      queryClient.invalidateQueries({ queryKey: ["putting-results"] });
      queryClient.invalidateQueries({ queryKey: ["training-results"] });
    }
  });

  return { deletePuttingResult, isDeleting: isPending };
};
